import { useEffect, useState, useCallback } from "react";
import { api, API_BASE, formatApiError } from "@/lib/api";
import { rupiah, tanggalID } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/app/Layout";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { PieChart, Plus, Trash2, Save, Download, FileSpreadsheet, PiggyBank, Users, History, Lock, Unlock } from "lucide-react";

const download = async (path, params, filename) => {
  const token = localStorage.getItem("khn_token");
  const url = new URL(`${API_BASE}${path}`);
  Object.entries(params).forEach(([k, v]) => v && url.searchParams.set(k, v));
  const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
  const blob = await res.blob();
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob); a.download = filename; a.click();
};

export default function Dividen() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [tahun, setTahun] = useState(new Date().getFullYear());
  const [partners, setPartners] = useState([]);
  const [ditahan, setDitahan] = useState(0);
  const [compute, setCompute] = useState(null);
  const [history, setHistory] = useState([]);
  const [catatan, setCatatan] = useState("");
  const [catatPengeluaran, setCatatPengeluaran] = useState(true);
  const [saving, setSaving] = useState(false);
  const [closings, setClosings] = useState([]);
  const lockedYears = new Set(closings.map((c) => c.tahun));

  const totalPct = Number(ditahan || 0) + partners.reduce((s, p) => s + Number(p.persen || 0), 0);
  const pctValid = Math.abs(totalPct - 100) <= 0.01;

  const loadSettings = useCallback(async () => {
    const r = await api.get("/dividend/settings");
    setDitahan(r.data.laba_ditahan_persen || 0);
    setPartners((r.data.partners || []).map(p => ({ ...p })));
  }, []);

  const loadCompute = useCallback(async () => {
    const r = await api.get("/dividend/compute", { params: { tahun } });
    setCompute(r.data);
  }, [tahun]);

  const loadHistory = useCallback(async () => {
    const r = await api.get("/dividend/history");
    setHistory(r.data || []);
  }, []);

  const loadClosings = useCallback(async () => {
    const r = await api.get("/dividend/closings");
    setClosings(r.data || []);
  }, []);

  useEffect(() => { loadSettings(); loadHistory(); loadClosings(); }, [loadSettings, loadHistory, loadClosings]);
  useEffect(() => { loadCompute(); }, [loadCompute]);

  const addPartner = () => setPartners([...partners, { nama: "", persen: 0 }]);
  const removePartner = (i) => setPartners(partners.filter((_, idx) => idx !== i));
  const updPartner = (i, key, val) => setPartners(partners.map((p, idx) => idx === i ? { ...p, [key]: val } : p));

  const saveSettings = async () => {
    if (!pctValid) { toast.error(`Total persentase harus 100% (sekarang ${totalPct.toFixed(2)}%)`); return; }
    for (const p of partners) {
      if (!p.nama?.trim()) { toast.error("Nama pemodal wajib diisi"); return; }
    }
    setSaving(true);
    try {
      await api.put("/dividend/settings", {
        laba_ditahan_persen: Number(ditahan),
        partners: partners.map(p => ({ id: p.id, nama: p.nama.trim(), persen: Number(p.persen) })),
      });
      toast.success("Pengaturan dividen tersimpan");
      await loadSettings(); await loadCompute();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const distribute = async () => {
    if (!compute || compute.laba_bersih <= 0) { toast.error("Tidak ada laba untuk dibagikan"); return; }
    if (!pctValid) { toast.error("Simpan pengaturan persentase (harus 100%) terlebih dulu"); return; }
    if (!window.confirm(`Catat pembagian dividen tahun ${tahun}? ${catatPengeluaran ? "Dividen akan otomatis dicatat sebagai pengeluaran." : ""}`)) return;
    try {
      await api.post("/dividend/distribute", { tahun, catatan, catat_pengeluaran: catatPengeluaran });
      toast.success("Pembagian dividen berhasil dicatat");
      setCatatan("");
      await loadHistory(); await loadCompute();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const delDist = async (id) => {
    if (!window.confirm("Batalkan pembagian ini? Pengeluaran dividen terkait akan dihapus (kas kembali).")) return;
    try {
      const r = await api.delete(`/dividend/history/${id}`);
      toast.success(`Dibatalkan (${r.data.reversed_expenses} pengeluaran dividen dihapus)`);
      await loadHistory(); await loadCompute(); await loadClosings();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const toggleLock = async (th, locked) => {
    const msg = locked
      ? `Kunci tahun buku ${th}? Transaksi tahun tersebut tidak dapat diubah.`
      : `Buka kunci tahun buku ${th}? Transaksi tahun tersebut bisa diubah lagi (pembagian dividen tetap tersimpan).`;
    if (!window.confirm(msg)) return;
    try {
      await api.post("/dividend/closings/toggle", { tahun: th, locked });
      toast.success(`Tahun buku ${th} ${locked ? "dikunci" : "dibuka"}`);
      await loadClosings();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (<>
    <PageHeader title="Pembagian Dividen" subtitle="Bagi laba bersih (berbasis kas riil) ke laba ditahan & para pemodal."
      actions={<>
        <Button variant="outline" onClick={() => download("/dividend/export", { fmt: "pdf", tahun }, `dividen-${tahun}.pdf`)} className="rounded-sm" data-testid="dividen-dl-pdf"><Download size={14} className="mr-1" /> PDF</Button>
        <Button variant="outline" onClick={() => download("/dividend/export", { fmt: "xlsx", tahun }, `dividen-${tahun}.xlsx`)} className="rounded-sm" data-testid="dividen-dl-xlsx"><FileSpreadsheet size={14} className="mr-1" /> Excel</Button>
      </>} />

    <div className="p-4 md:p-8 space-y-6">
      {/* Tahun */}
      <div className="card-flat p-4 max-w-xs">
        <Label>Tahun Buku</Label>
        <Input type="number" value={tahun} onChange={(e) => setTahun(Number(e.target.value) || tahun)} data-testid="dividen-tahun" />
      </div>

      {/* Settings */}
      <div className="card-flat p-5" data-testid="dividen-settings-card">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-base font-semibold flex items-center gap-2 text-slate-800"><PieChart size={18} className="brand-text" /> Pengaturan Persentase</h2>
          {!isAdmin && <span className="text-xs text-amber-600 flex items-center gap-1"><Lock size={12} /> Hanya admin dapat mengubah</span>}
        </div>

        <div className="space-y-3">
          <div className="flex items-center gap-3 flex-wrap md:flex-nowrap">
            <div className="flex items-center gap-2 min-w-0 flex-1">
              <PiggyBank size={16} className="text-emerald-600 shrink-0" />
              <span className="text-sm font-medium text-slate-700 truncate">Laba Ditahan (perusahaan)</span>
            </div>
            <div className="flex items-center gap-1">
              <Input type="number" min="0" max="100" step="0.01" value={ditahan} disabled={!isAdmin}
                onChange={(e) => setDitahan(e.target.value)} className="w-24 text-right" data-testid="dividen-ditahan" />
              <span className="text-sm text-slate-500">%</span>
            </div>
          </div>

          {partners.map((p, i) => (
            <div key={i} className="flex items-center gap-3 flex-wrap md:flex-nowrap" data-testid={`dividen-partner-row-${i}`}>
              <div className="flex items-center gap-2 min-w-0 flex-1">
                <Users size={16} className="text-blue-600 shrink-0" />
                <Input placeholder="Nama pemodal" value={p.nama} disabled={!isAdmin}
                  onChange={(e) => updPartner(i, "nama", e.target.value)} data-testid={`dividen-partner-nama-${i}`} />
              </div>
              <div className="flex items-center gap-1">
                <Input type="number" min="0" max="100" step="0.01" value={p.persen} disabled={!isAdmin}
                  onChange={(e) => updPartner(i, "persen", e.target.value)} className="w-24 text-right" data-testid={`dividen-partner-persen-${i}`} />
                <span className="text-sm text-slate-500">%</span>
                {isAdmin && <button onClick={() => removePartner(i)} className="ml-1 text-rose-500 hover:text-rose-700 p-1" data-testid={`dividen-partner-del-${i}`}><Trash2 size={15} /></button>}
              </div>
            </div>
          ))}
        </div>

        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mt-4 pt-4 border-t">
          <div className="flex items-center gap-3 flex-wrap">
            {isAdmin && <Button variant="outline" size="sm" onClick={addPartner} className="rounded-sm" data-testid="dividen-add-partner"><Plus size={14} className="mr-1" /> Tambah Pemodal</Button>}
            <span className={`text-sm font-semibold px-2.5 py-1 rounded-sm ${pctValid ? "bg-emerald-100 text-emerald-700" : "bg-rose-100 text-rose-700"}`} data-testid="dividen-total-pct">
              Total: {totalPct.toFixed(2)}%
            </span>
          </div>
          {isAdmin && <Button onClick={saveSettings} disabled={saving || !pctValid} className="rounded-sm w-full sm:w-auto" data-testid="dividen-save-settings"><Save size={14} className="mr-1" /> Simpan Pengaturan</Button>}
        </div>
      </div>

      {/* Perhitungan */}
      {compute && (
        <div className="card-flat p-5" data-testid="dividen-compute-card">
          <h2 className="text-base font-semibold mb-4 text-slate-800">Simulasi Pembagian — Tahun {compute.tahun}</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
            <Stat label="Kas Masuk (riil)" value={rupiah(compute.kas_masuk)} tone="slate" />
            <Stat label="Total Pengeluaran" value={rupiah(compute.total_pengeluaran)} tone="rose" />
            <Stat label="Laba Bersih (Kas)" value={rupiah(compute.laba_bersih)} tone={compute.laba_bersih >= 0 ? "emerald" : "rose"} testid="dividen-laba-bersih" />
            <Stat label="Total Dividen" value={rupiah(compute.total_dividen_nominal)} tone="blue" />
          </div>

          <div className="overflow-x-auto -mx-1">
          <table className="w-full text-sm min-w-[420px]">
            <thead>
              <tr className="bg-slate-50 border-y text-slate-500 text-xs uppercase tracking-wide">
                <th className="p-3 text-left">Penerima</th><th className="p-3 text-right">Persentase</th><th className="p-3 text-right">Nominal</th>
              </tr>
            </thead>
            <tbody>
              <tr className="border-b bg-emerald-50/50">
                <td className="p-3"><span className="flex items-center gap-2"><PiggyBank size={15} className="text-emerald-600 shrink-0" /> Laba Ditahan (perusahaan)</span></td>
                <td className="p-3 text-right whitespace-nowrap">{compute.laba_ditahan_persen}%</td>
                <td className="p-3 text-right font-mono whitespace-nowrap">{rupiah(compute.laba_ditahan_nominal)}</td>
              </tr>
              {compute.alokasi.map((a, i) => (
                <tr key={i} className="border-b" data-testid={`dividen-alokasi-${i}`}>
                  <td className="p-3"><span className="flex items-center gap-2"><Users size={15} className="text-blue-600 shrink-0" /> {a.nama}</span></td>
                  <td className="p-3 text-right whitespace-nowrap">{a.persen}%</td>
                  <td className="p-3 text-right font-mono whitespace-nowrap">{rupiah(a.nominal)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>

          {!compute.settings_valid && (
            <p className="mt-3 text-sm text-rose-600" data-testid="dividen-invalid-warning">Persentase belum berjumlah 100% — simpan pengaturan yang benar sebelum mencatat pembagian.</p>
          )}
          {compute.laba_bersih <= 0 && (
            <p className="mt-3 text-sm text-amber-600">Belum ada laba positif untuk dibagikan pada tahun ini.</p>
          )}

          {isAdmin && (
            <div className="mt-5 pt-4 border-t space-y-3">
              <div>
                <Label>Catatan (opsional)</Label>
                <Input value={catatan} onChange={(e) => setCatatan(e.target.value)} placeholder="mis. Pembagian dividen tutup buku 2026" data-testid="dividen-catatan" />
              </div>
              <label className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer">
                <input type="checkbox" checked={catatPengeluaran} onChange={(e) => setCatatPengeluaran(e.target.checked)} data-testid="dividen-catat-pengeluaran" />
                Catat dividen sebagai pengeluaran otomatis (kas berkurang)
              </label>
              <Button onClick={distribute} disabled={!compute.settings_valid || compute.laba_bersih <= 0} className="rounded-sm" data-testid="dividen-distribute-btn">
                Catat Pembagian
              </Button>
              <p className="text-xs text-amber-600 flex items-center gap-1"><Lock size={12} /> Mencatat pembagian akan <b>mengunci tahun buku {tahun}</b> — transaksi tahun tersebut tidak dapat diubah/dihapus sampai pembagian dibatalkan.</p>
            </div>
          )}
        </div>
      )}

      {/* Riwayat */}
      <div className="card-flat p-5" data-testid="dividen-history-card">
        <h2 className="text-base font-semibold mb-4 flex items-center gap-2 text-slate-800"><History size={18} className="brand-text" /> Riwayat Pembagian</h2>
        {history.length === 0 ? (
          <p className="text-sm text-slate-400">Belum ada riwayat pembagian dividen.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[720px]">
              <thead>
                <tr className="bg-slate-50 border-y text-slate-500 text-xs uppercase tracking-wide">
                  <th className="p-3 text-left">Tanggal</th><th className="p-3 text-left">Tahun</th>
                  <th className="p-3 text-right">Laba Bersih</th><th className="p-3 text-right">Total Dividen</th>
                  <th className="p-3 text-left">Penerima</th><th className="p-3 text-left">Catatan</th>
                  {isAdmin && <th className="p-3 text-right">Aksi</th>}
                </tr>
              </thead>
              <tbody>
                {history.map((h) => {
                  const locked = lockedYears.has(h.tahun);
                  return (
                  <tr key={h.id} className="border-b align-top" data-testid={`dividen-history-row`}>
                    <td className="p-3 whitespace-nowrap">{tanggalID(h.tanggal)}</td>
                    <td className="p-3 whitespace-nowrap">{h.tahun}{" "}
                      {locked
                        ? <span className="inline-flex items-center gap-0.5 text-[10px] text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded" title="Tahun buku terkunci"><Lock size={10}/> terkunci</span>
                        : <span className="inline-flex items-center gap-0.5 text-[10px] text-emerald-600 bg-emerald-50 px-1.5 py-0.5 rounded" title="Tahun buku terbuka"><Unlock size={10}/> terbuka</span>}
                    </td>
                    <td className="p-3 text-right font-mono whitespace-nowrap">{rupiah(h.laba_bersih)}</td>
                    <td className="p-3 text-right font-mono brand-text whitespace-nowrap">{rupiah(h.total_dividen_nominal)}</td>
                    <td className="p-3">
                      <div className="text-xs text-slate-500">Ditahan {h.laba_ditahan_persen}%: {rupiah(h.laba_ditahan_nominal)}</div>
                      {(h.alokasi || []).map((a, i) => (<div key={i} className="text-xs">{a.nama} ({a.persen}%): {rupiah(a.nominal)}</div>))}
                    </td>
                    <td className="p-3 text-slate-500">{h.catatan || "-"}{h.catat_pengeluaran ? "" : " (tanpa pengeluaran)"}</td>
                    {isAdmin && <td className="p-3">
                      <div className="flex items-center justify-end gap-1">
                        <button onClick={() => toggleLock(h.tahun, !locked)} className={`p-1 ${locked ? "text-emerald-600 hover:text-emerald-800" : "text-amber-600 hover:text-amber-800"}`} title={locked ? "Buka kunci tahun buku" : "Kunci tahun buku"} data-testid="dividen-lock-toggle">
                          {locked ? <Unlock size={15}/> : <Lock size={15}/>}
                        </button>
                        <button onClick={() => delDist(h.id)} className="text-rose-500 hover:text-rose-700 p-1" title="Batalkan pembagian" data-testid="dividen-history-del"><Trash2 size={15} /></button>
                      </div>
                    </td>}
                  </tr>
                );})}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  </>);
}

function Stat({ label, value, tone = "slate", testid }) {
  const tones = {
    slate: "bg-slate-50 text-slate-800", rose: "bg-rose-50 text-rose-700",
    emerald: "bg-emerald-50 text-emerald-700", blue: "bg-blue-50 text-blue-700",
  };
  return (
    <div className={`rounded-sm p-3 ${tones[tone]}`} data-testid={testid}>
      <div className="text-[11px] uppercase tracking-wide opacity-70">{label}</div>
      <div className="text-base md:text-lg font-semibold font-mono mt-0.5">{value}</div>
    </div>
  );
}
